<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  rate: { type: Object, default: () => ({}) },
  isSubmitting: { type: Boolean, default: false },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();
const getters = useStoreGetters();

const activeTab = ref('general');

// Dados Gerais
const name = ref('');
const active = ref(true);
const promoCodeOnly = ref(false);
const showInCalendar = ref(true);
const pensionType = ref('room_only');
const minStay = ref(1);
const maxStay = ref(0);
const advanceDays = ref(0);
const lastminuteDays = ref(0);
const richDescription = ref('');

// Garantias e Cancelamento
const guaranteeType = ref('percentage');
const guaranteePercentage = ref(0);
const guaranteeDescription = ref('');
const nonRefundable = ref(false);
const cancellationDescription = ref('');
const cancellationPenalties = ref([]);

// Pagamento
const paymentMethods = ref([]);

// Extras Inclusos
const includedExtras = ref([]);

const extras = computed(() => getters['motorReservaExtras/getRecords']?.value || []);

const pensionTypes = [
  { value: 'room_only', label: 'Somente Hospedagem' },
  { value: 'breakfast', label: 'Café da Manhã' },
  { value: 'half_board', label: 'Meia Pensão' },
  { value: 'full_board', label: 'Pensão Completa' },
  { value: 'all_inclusive', label: 'All Inclusive' },
];

const guaranteeTypes = [
  { value: 'percentage', label: 'Percentual da Reserva' },
  { value: 'first_night', label: 'Primeira Diária' },
  { value: 'full', label: 'Valor Total' },
  { value: 'none', label: 'Sem Garantia' },
];

const availablePaymentMethods = [
  'Cartão de Crédito',
  'Pagamento Via PIX',
  'Pagamento por Depósito',
  'Pagamento Direto no Hotel',
];

const tabs = [
  { key: 'general', label: 'Dados Gerais' },
  { key: 'guarantees', label: 'Garantias e Cancelamento' },
  { key: 'payment', label: 'Configurações de Pagamento' },
  { key: 'extras', label: 'Extras Inclusos' },
];

watch(
  () => props.rate,
  val => {
    if (val && val.id) {
      name.value = val.name || '';
      active.value = val.active !== false;
      promoCodeOnly.value = val.promo_code_only || false;
      showInCalendar.value = val.show_in_calendar !== false;
      pensionType.value = val.pension_type || 'room_only';
      minStay.value = val.min_stay || 1;
      maxStay.value = val.max_stay || 0;
      advanceDays.value = val.advance_days || 0;
      lastminuteDays.value = val.lastminute_days || 0;
      richDescription.value = val.rich_description || '';
      guaranteeType.value = val.guarantee_type || 'percentage';
      guaranteePercentage.value = val.guarantee_percentage || 0;
      guaranteeDescription.value = val.guarantee_description || '';
      nonRefundable.value = val.non_refundable || false;
      cancellationDescription.value = val.cancellation_description || '';
      cancellationPenalties.value = [...(val.cancellation_penalties || [])];
      paymentMethods.value = [...(val.payment_methods || [])];
      includedExtras.value = [...(val.included_extras || [])];
    }
  },
  { immediate: true }
);

const togglePaymentMethod = method => {
  const i = paymentMethods.value.indexOf(method);
  if (i >= 0) paymentMethods.value.splice(i, 1);
  else paymentMethods.value.push(method);
};

const toggleExtra = extraName => {
  const i = includedExtras.value.indexOf(extraName);
  if (i >= 0) includedExtras.value.splice(i, 1);
  else includedExtras.value.push(extraName);
};

const addPenalty = () => {
  cancellationPenalties.value.push({ days_before: 1, penalty_type: 'percentage', percentage: 100 });
};
const removePenalty = idx => {
  cancellationPenalties.value.splice(idx, 1);
};

const handleSubmit = () => {
  if (!name.value.trim()) return;
  emit('submit', {
    motor_reserva_rate: {
      name: name.value.trim(),
      active: active.value,
      promo_code_only: promoCodeOnly.value,
      show_in_calendar: showInCalendar.value,
      pension_type: pensionType.value,
      min_stay: Number(minStay.value),
      max_stay: Number(maxStay.value),
      advance_days: Number(advanceDays.value),
      lastminute_days: Number(lastminuteDays.value),
      rich_description: richDescription.value,
      guarantee_type: guaranteeType.value,
      guarantee_percentage: Number(guaranteePercentage.value),
      guarantee_description: guaranteeDescription.value,
      non_refundable: nonRefundable.value,
      cancellation_description: cancellationDescription.value,
      cancellation_penalties: cancellationPenalties.value,
      payment_methods: paymentMethods.value,
      included_extras: includedExtras.value,
    },
  });
};

const ic = 'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:border-woot-500 focus:outline-none';
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <!-- Tabs -->
    <div class="flex gap-1 border-b border-slate-200 dark:border-slate-600">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
        :class="
          activeTab === tab.key
            ? 'border-woot-500 text-woot-500'
            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
        "
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Tab: Dados Gerais -->
    <div v-show="activeTab === 'general'" class="flex flex-col gap-4">
      <div>
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Nome</label>
        <input v-model="name" type="text" required :class="ic" placeholder="Ex: Tarifa com Café da Manhã..." />
      </div>

      <div class="flex flex-col gap-2">
        <label class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input v-model="active" type="checkbox" class="rounded border-slate-300 text-woot-500" />
          Ativa
        </label>
        <label class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input v-model="promoCodeOnly" type="checkbox" class="rounded border-slate-300 text-woot-500" />
          Mostrar esta tarifa somente com código promocional
        </label>
        <label class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input v-model="showInCalendar" type="checkbox" class="rounded border-slate-300 text-woot-500" />
          Exibir tarifa no calendário inteligente
        </label>
      </div>

      <div>
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Tipo de Pensão</label>
        <select v-model="pensionType" :class="ic">
          <option v-for="pt in pensionTypes" :key="pt.value" :value="pt.value">{{ pt.label }}</option>
        </select>
      </div>

      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4 class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">Configurações de Disponibilidade</h4>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-xs text-slate-500 mb-1">Estadia mínima padrão</label>
            <input v-model.number="minStay" type="number" min="1" :class="ic" />
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">Estadia máxima padrão</label>
            <input v-model.number="maxStay" type="number" min="0" :class="ic" />
            <span class="text-xs text-slate-400">0 = sem limite</span>
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">Dias tarifa antecipada</label>
            <input v-model.number="advanceDays" type="number" min="0" :class="ic" />
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">Dias tarifa Last Minute</label>
            <input v-model.number="lastminuteDays" type="number" min="0" :class="ic" />
          </div>
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Texto Descritivo</label>
        <textarea v-model="richDescription" rows="4" :class="ic" placeholder="Descreva a tarifa em detalhes..." />
      </div>
    </div>

    <!-- Tab: Garantias e Cancelamento -->
    <div v-show="activeTab === 'guarantees'" class="flex flex-col gap-4">
      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4 class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">Garantias</h4>

        <div class="flex flex-col gap-3">
          <div>
            <label class="block text-xs text-slate-500 mb-1">Selecione o tipo de garantia:</label>
            <select v-model="guaranteeType" :class="ic">
              <option v-for="gt in guaranteeTypes" :key="gt.value" :value="gt.value">{{ gt.label }}</option>
            </select>
          </div>

          <div v-if="guaranteeType === 'percentage'">
            <label class="block text-xs text-slate-500 mb-1">Percentual:</label>
            <div class="flex items-center gap-2">
              <input v-model.number="guaranteePercentage" type="number" min="0" max="100" step="0.01" class="w-24 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm" />
              <span class="text-sm text-slate-500">%</span>
            </div>
          </div>

          <div>
            <label class="block text-xs text-slate-500 mb-1">Descrição da garantia</label>
            <textarea v-model="guaranteeDescription" rows="3" :class="ic" placeholder="Descreva as condições de garantia..." />
          </div>
        </div>
      </div>

      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4 class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">Políticas de Cancelamento</h4>

        <label class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 mb-3">
          <input v-model="nonRefundable" type="checkbox" class="rounded border-slate-300 text-red-500" />
          Esta é uma tarifa não-reembolsável
        </label>

        <div class="flex flex-col gap-3">
          <div>
            <label class="block text-xs text-slate-500 mb-1">Descrição do cancelamento</label>
            <textarea v-model="cancellationDescription" rows="3" :class="ic" placeholder="Descreva a política de cancelamento..." />
          </div>

          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="text-xs font-medium text-slate-600 dark:text-slate-300">Faixas de penalidades de cancelamento</label>
              <button type="button" class="text-xs text-woot-500 hover:underline" @click="addPenalty">
                + Adicionar Penalidade
              </button>
            </div>

            <div v-for="(penalty, idx) in cancellationPenalties" :key="idx" class="flex items-center gap-2 mb-2 p-2 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <span class="text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">Cancelando em até</span>
              <input v-model.number="penalty.days_before" type="number" min="0" class="w-16 px-2 py-1 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800" />
              <span class="text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">dias antes do checkin</span>
              <span class="text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">Percentual:</span>
              <input v-model.number="penalty.percentage" type="number" min="0" max="100" class="w-16 px-2 py-1 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800" />
              <span class="text-xs text-slate-500">%</span>
              <button type="button" class="text-xs text-red-500 hover:text-red-700 whitespace-nowrap" @click="removePenalty(idx)">
                Remover
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab: Configurações de Pagamento -->
    <div v-show="activeTab === 'payment'" class="flex flex-col gap-4">
      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4 class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">Formas de Pagamento Liberadas</h4>
        <p class="text-xs text-slate-500 mb-3">Apenas as formas de pagamento selecionadas abaixo são aceitas para esta tarifa:</p>

        <div class="flex flex-col gap-2">
          <label
            v-for="method in availablePaymentMethods"
            :key="method"
            class="flex items-center gap-2 py-1.5 px-3 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 text-sm"
            :class="paymentMethods.includes(method) ? 'text-woot-600 font-medium bg-woot-50 dark:bg-woot-900/20' : 'text-slate-700 dark:text-slate-300'"
          >
            <input
              type="checkbox"
              :checked="paymentMethods.includes(method)"
              class="rounded border-slate-300 text-woot-500"
              @change="togglePaymentMethod(method)"
            />
            {{ method }}
          </label>
        </div>
      </div>
    </div>

    <!-- Tab: Extras Inclusos -->
    <div v-show="activeTab === 'extras'" class="flex flex-col gap-4">
      <div class="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
        <h4 class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">Extras Incluídos Nesta Tarifa</h4>
        <p class="text-xs text-slate-500 mb-3">
          Selecione os extras que já estão inclusos nesta tarifa.
          <span v-if="includedExtras.length" class="font-medium text-woot-500">({{ includedExtras.length }}) selecionados</span>
        </p>

        <div v-if="extras.length" class="flex flex-col gap-1">
          <label
            v-for="extra in extras"
            :key="extra.id"
            class="flex items-center gap-2 py-1.5 px-3 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 text-sm"
            :class="includedExtras.includes(extra.name) ? 'text-woot-600 font-medium bg-woot-50 dark:bg-woot-900/20' : 'text-slate-700 dark:text-slate-300'"
          >
            <input
              type="checkbox"
              :checked="includedExtras.includes(extra.name)"
              class="rounded border-slate-300 text-woot-500"
              @change="toggleExtra(extra.name)"
            />
            {{ extra.name }}
          </label>
        </div>

        <p v-else class="text-sm text-slate-500 text-center py-4">
          Nenhum extra cadastrado. Cadastre extras na aba "Extras" primeiro.
        </p>
      </div>
    </div>

    <!-- Ações -->
    <div class="flex items-center justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-600">
      <Button label="Cancelar" variant="secondary" size="sm" @click="emit('cancel')" />
      <Button
        label="Salvar Tarifa"
        size="sm"
        :disabled="!name.trim().length || isSubmitting"
        :is-loading="isSubmitting"
        @click="handleSubmit"
      />
    </div>
  </form>
</template>
